import {useLegacyGrpcClientConfig} from './GrpcClientContext';
import {InferResponseType, MakeAllKeysRequired} from '../types';
import {
  useGenericGrpcRequest,
  GrpcQueryConfig,
  GrpcRequestServiceMethod,
} from './useGenericGrpcRequest';
import {DataFetcherRequest} from '../useRequest';
import {convertAllNullPropertiesToUndefined} from './convertAllNullPropertiesToUndefined';

// not importing the original Device enum here due to bundle size regression
// import {Device} from '@aboutyou/grpc-clients/grips/protos/shared/shared.types';
export enum GripsDevice {
  MOBILE_WEB = 3,
  DESKTOP = 4,
}

export type GenericGripsConfig = {
  // intentional empty so that the configs can be passed inline for legacy Grips requests
  // In Grips there is no definite shape for configs, so we can't use a single type here
  device?: GripsDevice;
  shopId?: number;
  childShopId?: number;
  deprecatedParentShopId?: number;
};

/**
 * This should be used only for legacy grips service methods which are not yet migrated to Tadarida backend.
 * This will be removed in the future.
 * A convenience wrapper around `useRequest` to do GRPC requests.
 * The GRPC `channel` will be read from context.
 * @param ServiceMethod the GRPC service method to call
 * @param params the parameters for the specific GRPC function
 */
export function useLegacyGrpcRequest<
  ServiceMethod extends GrpcRequestServiceMethod,
  ResponseType extends InferResponseType<ReturnType<ServiceMethod>>,
  ParamsType extends MakeAllKeysRequired<
    Omit<Parameters<ServiceMethod>[1], keyof GenericGripsConfig>
  >,
>(
  maybeAsyncService: ServiceMethod,
  params: ParamsType,
  grpcQueryConfig: GrpcQueryConfig<ResponseType, ParamsType> = {},
): DataFetcherRequest<ResponseType> {
  const {shopId, childShopId, device} = useLegacyGrpcClientConfig();
  const genericConfig = {
    shopId,
    childShopId,
    device: device === 'mobile' ? GripsDevice.MOBILE_WEB : GripsDevice.DESKTOP,
  };

  const query = useGenericGrpcRequest(
    maybeAsyncService,
    convertAllNullPropertiesToUndefined<ParamsType>(params),
    genericConfig,
    grpcQueryConfig,
  );

  return query as DataFetcherRequest<ResponseType>;
}
